import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface DocumentChapter {
  id: string;
  content_id: string;
  title: string;
  summary: string | null;
  start_page: number;
  end_page: number;
  order_index: number;
  created_at: string;
}

export function useDocumentChapters(contentId: string | undefined) {
  return useQuery({
    queryKey: ['document-chapters', contentId],
    queryFn: async () => {
      if (!contentId) return [];
      
      const { data, error } = await supabase
        .from('document_chapters')
        .select('*')
        .eq('content_id', contentId)
        .order('start_page', { ascending: true });
      
      if (error) throw error;
      return data as DocumentChapter[];
    },
    enabled: !!contentId,
  });
}

export function useDocumentChaptersStatus(contentId: string | undefined) {
  const queryClient = useQueryClient();
  const [realtimeStatus, setRealtimeStatus] = useState<string | null>(null);

  // Initial fetch
  const query = useQuery({
    queryKey: ['document-chapters-status', contentId],
    queryFn: async () => {
      if (!contentId) return null;
      
      const { data, error } = await supabase
        .from('content')
        .select('chapters_status')
        .eq('id', contentId)
        .single();
      
      if (error) throw error;
      return data?.chapters_status as string | null;
    },
    enabled: !!contentId,
  });

  // Realtime subscription for status updates
  useEffect(() => {
    if (!contentId) return;

    const channel = supabase
      .channel(`doc-chapters-status-${contentId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'content',
          filter: `id=eq.${contentId}`,
        },
        (payload) => {
          const newStatus = (payload.new as any).chapters_status;
          console.log('[useDocumentChaptersStatus] Realtime update:', newStatus);
          setRealtimeStatus(newStatus);
          
          // If status changed to 'ready', refetch chapters data
          if (newStatus === 'ready') {
            queryClient.invalidateQueries({ queryKey: ['document-chapters', contentId] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [contentId, queryClient]);

  // Return realtime status if available, otherwise query data
  return {
    ...query,
    data: realtimeStatus ?? query.data,
  };
}

export function useGenerateDocumentChapters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contentId, documentText }: { contentId: string; documentText?: string }) => {
      const { data, error } = await supabase.functions.invoke('generate-document-chapters', {
        body: { contentId, documentText },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { contentId }) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['document-chapters', contentId] });
      queryClient.invalidateQueries({ queryKey: ['document-chapters-status', contentId] });
    },
  });
}